import React, { useState, useEffect } from 'react';
import {
  Upload,
  Button,
  message,
  List,
  Popconfirm,
  Typography,
  Space
} from 'antd';
import {
  UploadOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileOutlined
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import { Attachment } from '../services/lead';
import { uploadAttachment, deleteAttachment, getEntityAttachments } from '../services/attachment';

const { Text } = Typography;

interface AttachmentUploadProps {
  entityType: string;
  entityId?: number;
  maxCount?: number;
  disabled?: boolean;
  onChange?: (attachments: Attachment[]) => void;
}

const AttachmentUpload: React.FC<AttachmentUploadProps> = ({
  entityType,
  entityId,
  maxCount = 5,
  disabled = false,
  onChange
}) => {
  const [uploading, setUploading] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  // Load existing attachments when entityId is provided
  useEffect(() => {
    if (entityId) {
      loadAttachments();
    }
  }, [entityId]);

  const loadAttachments = async () => {
    if (!entityId) return;

    try {
      const data = await getEntityAttachments(entityType, entityId);
      setAttachments(data);
      onChange?.(data);

      // Convert to UploadFile format for display
      const uploadFiles: UploadFile[] = data.map(att => ({
        uid: att.id.toString(),
        name: att.filename || att.file_name || '未知文件',
        status: 'done',
        url: att.file_path,
        size: att.file_size
      }));
      setFileList(uploadFiles);
    } catch (error) {
      console.error('Failed to load attachments:', error);
    }
  };

  const handleUpload = async (file: File): Promise<boolean> => {
    if (!entityId) {
      message.error('无法上传附件：实体ID缺失');
      return false;
    }

    // Check file size (50MB limit)
    if (file.size > 50 * 1024 * 1024) {
      message.error('文件大小不能超过50MB');
      return false;
    }

    // Check max count
    if (attachments.length >= maxCount) {
      message.error(`最多只能上传${maxCount}个附件`);
      return false;
    }

    setUploading(true);
    try {
      const attachment = await uploadAttachment(file, entityType, entityId);
      const newAttachments = [...attachments, attachment];
      setAttachments(newAttachments);
      onChange?.(newAttachments);

      // Add to file list
      const newFile: UploadFile = {
        uid: attachment.id.toString(),
        name: attachment.filename || attachment.file_name || '未知文件',
        status: 'done',
        url: attachment.file_path,
        size: attachment.file_size
      };
      setFileList([...fileList, newFile]);

      message.success('上传成功');
    } catch (error: any) {
      console.error('Upload failed:', error);
      message.error('上传失败：' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }

    return false; // Prevent default upload behavior
  };

  const handleDelete = async (attachment: Attachment) => {
    try {
      await deleteAttachment(attachment.id);
      const newAttachments = attachments.filter(a => a.id !== attachment.id);
      setAttachments(newAttachments);
      onChange?.(newAttachments);

      // Remove from file list
      setFileList(fileList.filter(f => f.uid !== attachment.id.toString()));

      message.success('删除成功');
    } catch (error: any) {
      console.error('Delete failed:', error);
      message.error('删除失败：' + (error.response?.data?.detail || error.message));
    }
  };

  const handlePreview = (attachment: Attachment) => {
    window.open(attachment.file_path, '_blank');
  };

  const formatFileSize = (size?: number): string => {
    if (!size) return '';
    if (size < 1024) return `${size}B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)}KB`;
    return `${(size / (1024 * 1024)).toFixed(1)}MB`;
  };

  const uploadProps: UploadProps = {
    beforeUpload: handleUpload,
    showUploadList: false,
    multiple: true,
    disabled: disabled || uploading || attachments.length >= maxCount,
  };

  return (
    <div>
      <Space direction="vertical" style={{ width: '100%' }}>
        {!disabled && (
          <Upload {...uploadProps}>
            <Button
              icon={<UploadOutlined />}
              loading={uploading}
              disabled={disabled || attachments.length >= maxCount}
            >
              {uploading ? '上传中...' : '上传附件'}
            </Button>
          </Upload>
        )}

        {attachments.length > 0 && (
          <List
            size="small"
            dataSource={attachments}
            renderItem={(attachment) => (
              <List.Item
                actions={[
                  <Button
                    key="preview"
                    type="text"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => handlePreview(attachment)}
                  >
                    预览
                  </Button>,
                  <Popconfirm
                    key="delete"
                    title="确定要删除这个附件吗？"
                    onConfirm={() => handleDelete(attachment)}
                    okText="确定"
                    cancelText="取消"
                    disabled={disabled}
                  >
                    <Button
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      danger
                      disabled={disabled}
                    >
                      删除
                    </Button>
                  </Popconfirm>
                ]}
              >
                <List.Item.Meta
                  avatar={<FileOutlined />}
                  title={
                    <Space>
                      <Text>{attachment.file_name}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {formatFileSize(attachment.file_size)}
                      </Text>
                    </Space>
                  }
                  description={
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {attachment.uploader_name || '未知用户'} · {attachment.created_at ? new Date(attachment.created_at).toLocaleString() : '未知时间'}
                    </Text>
                  }
                />
              </List.Item>
            )}
          />
        )}

        {attachments.length === 0 && entityId && (
          <Text type="secondary">暂无附件</Text>
        )}

        {!disabled && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            支持上传最多{maxCount}个附件，单个文件最大50MB
          </Text>
        )}
      </Space>
    </div>
  );
};

export default AttachmentUpload;